import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Drawer, Tabs, Spin, Button } from 'antd';
import axios from 'axios';
import {
  X,
  CaretLeft,
  CaretRight,
  Building,
  Stethoscope,
  Note,
  Calendar,
  Warning,
} from '@phosphor-icons/react';
import Typography from '../../components/Typography';
import ReferralStatusStepper from '../../components/portal/ReferralStatusStepper';
import ActivityFeed from '../../components/portal/ActivityFeed';
import DocumentsTab from './DocumentsTab';
import ReferralMessages from './ReferralMessages';
import { formatDateLong, formatDOB, formatDateTime } from '../../components/portal/dateUtils';
import './referralDetail.scss';

const { TabPane } = Tabs;

const ReferralDetail = ({
  referral: initialReferral,
  referralId,
  onClose,
  onNavigate,
  hasPrevious,
  hasNext,
  currentOrganizationId,
  lastNotificationAt,
  cable,
  user,
  loggedIn,
  visible,
  sharedWith,
  onRevoke,
  revoking,
  afterVisibleChange,
  onMarkRead,
  onActivityRead,
  initialTab,
}) => {
  const [referral, setReferral] = useState(initialReferral);
  const [loading, setLoading] = useState(!initialReferral && !!referralId);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  const [hasUnreadMessages, setHasUnreadMessages] = useState(
    (initialReferral?.unread_messages_count || 0) > 0,
  );
  const [hasUnreadDocuments, setHasUnreadDocuments] = useState(
    (initialReferral?.unread_documents_count || 0) > 0,
  );

  // Deep-link: ?tab=messages from notification
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab === 'messages') {
      setActiveTab('messages');
      if (onMarkRead && referral?.slug) onMarkRead(referral.slug);
    }
  }, []);

  const isVisible = visible !== undefined ? visible : !!(referral || referralId);

  // Fetch full referral details if only ID provided, or sync with prop changes
  useEffect(() => {
    if (referralId && !initialReferral) {
      fetchReferralDetails();
    } else {
      setReferral(initialReferral);
      setHasUnreadMessages((initialReferral?.unread_messages_count || 0) > 0);
      setHasUnreadDocuments((initialReferral?.unread_documents_count || 0) > 0);
      setLoading(false);
    }
    setActiveTab(initialTab || 'overview');
  }, [referralId, initialReferral, initialTab]);

  // Keyboard navigation with arrow keys (only when drawer is visible)
  useEffect(() => {
    if (!isVisible || !onNavigate) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft' && hasPrevious) {
        onNavigate('prev');
      } else if (event.key === 'ArrowRight' && hasNext) {
        onNavigate('next');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, onNavigate, hasPrevious, hasNext]);

  const fetchReferralDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/v1/portal/referrals/${referralId}`);
      setReferral(response.data.referral);
    } catch (error) {
      // error handled by setting referral to null (shows error state)
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (direction) => {
    if (onNavigate) {
      onNavigate(direction);
    }
  };

  const handleUnread = useCallback((unread) => {
    setHasUnreadMessages(unread);
  }, []);

  const drawerWidth = window.innerWidth < 768 ? '100%' : 680;

  // Support both prop patterns: direct referral object or ID-based fetch
  const patientName = referral?.patient?.name || referral?.patient_name || 'Unknown Patient';
  const patientDob = referral?.patient?.dob || referral?.patient_dob;
  const statusName = referral?.status?.name || referral?.status;
  const providerName = referral?.referring_provider?.name || referral?.referring_organization;
  const doctorName = referral?.referring_provider?.doctor_name || referral?.referring_provider;
  const specialty = referral?.referring_provider?.specialty;

  return (
    <Drawer
      placement="right"
      visible={isVisible}
      onClose={onClose}
      width={drawerWidth}
      closable={false}
      className="referral-detail-drawer"
      bodyStyle={{ padding: 0 }}
      mask
      maskClosable
      maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      afterVisibleChange={afterVisibleChange}
    >
      {loading ? (
        <div className="referral-detail__loading">
          <Spin size="large" />
        </div>
      ) : referral ? (
        <div className="referral-detail">
          {/* Archived Banner */}
          {referral.archived && (
            <div className="referral-detail__archived-banner">
              <Warning size={20} weight="fill" />
              <div className="referral-detail__archived-banner-content">
                <Typography variant="text" bold>
                  This referral is archived
                </Typography>
                <Typography variant="text" size="small">
                  This referral is read-only. You cannot send new messages or upload documents.
                </Typography>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="referral-detail__header">
            <div className="referral-detail__header-left">
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => handleNavigate('prev')}
                  disabled={!hasPrevious}
                  className="referral-detail__nav-btn"
                  aria-label="Previous referral"
                >
                  <CaretLeft size={20} weight="bold" />
                </button>
              )}
            </div>

            <div className="referral-detail__header-center">
              <Typography variant="text" size="large" bold className="referral-detail__patient-name">
                {patientName}
              </Typography>
              {patientDob && (
                <Typography variant="text" size="small" className="referral-detail__dob">
                  DOB: {formatDOB(patientDob)}
                </Typography>
              )}
            </div>

            <div className="referral-detail__header-right">
              {onNavigate && (
                <button
                  type="button"
                  onClick={() => handleNavigate('next')}
                  disabled={!hasNext}
                  className="referral-detail__nav-btn"
                  aria-label="Next referral"
                >
                  <CaretRight size={20} weight="bold" />
                </button>
              )}
              <button type="button" onClick={onClose} className="referral-detail__close" aria-label="Close">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Shared With Banner (sharer view) */}
          {sharedWith && (
            <div className="referral-detail__shared-banner">
              <div className="referral-detail__shared-banner-info">
                <Typography variant="text" size="small" bold>
                  {'Shared with '}
                  {sharedWith.company_name}
                </Typography>
                <Typography variant="text" size="small">
                  {sharedWith.email}
                </Typography>
              </div>
              <Button
                size="small"
                onClick={onRevoke}
                loading={revoking}
              >
                Revoke Access
              </Button>
            </div>
          )}

          {/* Tabs */}
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              if (key === 'messages') {
                if (hasUnreadMessages && onActivityRead) onActivityRead();
                setHasUnreadMessages(false);
                if (onMarkRead && referral?.slug) onMarkRead(referral.slug);
              }
              if (key === 'documents') {
                if (hasUnreadDocuments && onActivityRead) onActivityRead();
                setHasUnreadDocuments(false);
              }
            }}
            className="referral-detail__tabs"
          >
            <TabPane tab="Overview" key="overview">
              <div className="referral-detail__content">
                {/* Status Stepper */}
                <ReferralStatusStepper
                  currentStatus={statusName}
                  statusDates={referral.status_dates}
                  hasFinalStep={referral.has_seen_step}
                />

                {/* Appointment Banner */}
                {referral.appointment_date && (
                  <div className="referral-detail__appointment-banner">
                    <Calendar size={18} weight="fill" />
                    <Typography variant="text" size="small" bold>
                      Appointment: {formatDateTime(referral.appointment_date)}
                    </Typography>
                  </div>
                )}

                {/* Info Cards */}
                <div className="referral-detail__cards">
                  {/* Practice & Provider */}
                  <div className="referral-detail__card referral-detail__card--blue">
                    <span className="referral-detail__card-label">
                      PRACTICE & PROVIDER
                    </span>
                    <span className="referral-detail__card-value referral-detail__card-value--bold">
                      {providerName || 'N/A'}
                    </span>
                    {doctorName && (
                      <span className="referral-detail__card-subtitle">
                        {doctorName}
                        {specialty && ` • ${specialty}`}
                      </span>
                    )}
                  </div>

                  {/* Referral Reason */}
                  {referral.referral_reason && (
                    <div className="referral-detail__card referral-detail__card--pink">
                      <span className="referral-detail__card-label">
                        REFERRAL REASON
                      </span>
                      <span className="referral-detail__card-value">
                        {referral.referral_reason}
                      </span>
                    </div>
                  )}

                  {/* Clinical Notes */}
                  {referral.clinical_notes && (
                    <div className="referral-detail__card referral-detail__card--neutral">
                      <span className="referral-detail__card-label">
                        CLINICAL NOTES
                      </span>
                      <span className="referral-detail__card-value">
                        {referral.clinical_notes}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </TabPane>

            <TabPane tab="Activity" key="activity">
              <ActivityFeed
                activities={referral.activities}
                lastUpdated={referral.last_activity_at}
              />
            </TabPane>

            <TabPane
              tab={
                <span>
                  Messages
                  {hasUnreadMessages && <span className="referral-detail__tab-dot" />}
                </span>
              }
              key="messages"
            >
              <ReferralMessages
                key={referral.slug}
                slug={referral?.slug}
                archived={referral?.archived}
                cable={cable}
                user={user}
                loggedIn={loggedIn}
                onUnread={handleUnread}
              />
            </TabPane>

            <TabPane
              tab={(
                <span>
                  Documents
                  {hasUnreadDocuments && <span className="referral-detail__tab-dot" />}
                </span>
              )}
              key="documents"
            >
              <DocumentsTab
                referralEntitySlug={referral?.slug}
                archived={referral?.archived}
                currentOrganizationId={currentOrganizationId}
                lastNotificationAt={lastNotificationAt}
              />
            </TabPane>
          </Tabs>
        </div>
      ) : (
        <div className="referral-detail__error">
          <Typography variant="text">Failed to load referral details</Typography>
        </div>
      )}
    </Drawer>
  );
};

ReferralDetail.propTypes = {
  // Support direct referral object (existing pattern)
  referral: PropTypes.shape({
    id: PropTypes.number,
    patient_name: PropTypes.string,
    patient_dob: PropTypes.string,
    patient: PropTypes.shape({
      name: PropTypes.string,
      dob: PropTypes.string,
    }),
    referring_organization: PropTypes.string,
    referring_provider: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
        doctor_name: PropTypes.string,
        specialty: PropTypes.string,
      }),
    ]),
    status: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        name: PropTypes.string,
      }),
    ]),
    status_dates: PropTypes.object,
    auth_status: PropTypes.string,
    pre_auth_status: PropTypes.shape({
      status: PropTypes.string,
      expires_at: PropTypes.string,
    }),
    referral_reason: PropTypes.string,
    clinical_notes: PropTypes.string,
    appointment_date: PropTypes.string,
    archived: PropTypes.bool,
    unread_messages_count: PropTypes.number,
    unread_documents_count: PropTypes.number,
  }),
  // Or support ID-based fetch pattern
  referralId: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onNavigate: PropTypes.func,
  hasPrevious: PropTypes.bool,
  hasNext: PropTypes.bool,
  currentOrganizationId: PropTypes.number,
  lastNotificationAt: PropTypes.number,
  cable: PropTypes.object,
  user: PropTypes.object,
  loggedIn: PropTypes.bool,
  visible: PropTypes.bool,
  sharedWith: PropTypes.shape({
    company_name: PropTypes.string,
    email: PropTypes.string,
    shared_by: PropTypes.string,
    shared_at: PropTypes.string,
    share_id: PropTypes.number,
  }),
  onRevoke: PropTypes.func,
  revoking: PropTypes.bool,
  afterVisibleChange: PropTypes.func,
  onActivityRead: PropTypes.func,
  initialTab: PropTypes.string,
};

ReferralDetail.defaultProps = {
  referral: null,
  referralId: null,
  onNavigate: null,
  hasPrevious: false,
  hasNext: false,
  currentOrganizationId: null,
  lastNotificationAt: null,
  cable: null,
  user: null,
  loggedIn: false,
  visible: undefined,
  sharedWith: null,
  onRevoke: null,
  revoking: false,
  afterVisibleChange: undefined,
  onActivityRead: null,
  initialTab: null,
};

export default connect((state) => ({
  currentOrganizationId: state.auth.currentOrganizationId,
  user: state.auth.data,
  loggedIn: !!state.auth.hasAuthCreds,
}))(ReferralDetail);
